/**
 * Pourquoi une compétence ne rend aucun énoncé — la phrase, en un seul endroit.
 *
 * **Ici et non dans un écran, parce qu'il y en a deux.** « Ce qui lui sera
 * proposé » le dit sous une liste d'exercices vide ; « Vu en classe, jamais
 * mesuré ici » le dit depuis le 8 septembre 2026, sur la remarque de Hedi
 * — *« je ne vois pas d'exercices correspondant à ces compétences »* — qui a
 * montré que la carte listait un manque sans jamais en donner la cause.
 *
 * **Dans `ui/` et non dans `parent/` depuis le 8 septembre 2026** : la coquille
 * enfant n'a pas le droit d'importer `parent/` (un test balaie `src/child/**`
 * pour l'affirmer) et l'écran du soir dit maintenant, lui aussi, pourquoi sa
 * journée n'a rien donné. Déplacé et non recopié — deux définitions de la même
 * phrase divergent, et l'enfant et son parent liraient alors deux raisons pour
 * un seul manque. Même arbitrage que `ui/periode` et `ui/planDuSoir`.
 *
 * La cause est **tranchée par le serveur** (`exercise.why_empty`) et jamais
 * redevinée ici : les filtres sont ceux du tirage, et une seconde cascade
 * dériverait de la première sans que rien ne dise laquelle a raison. Le
 * commentaire de `why_empty` note que celle-ci a déjà été redérivée trois fois.
 *
 * Chaque phrase dit deux choses : ce qui manque, et **si cela se débloquera
 * seul**. C'est la distinction que le message précédent — « déjà vu récemment,
 * ou pas sous la forme attendue » — écrasait : `deja_faits` s'éteint à une date
 * connue, `forme_inadaptee` n'évoluera jamais sans une génération.
 */

import { enClair } from "@/ui/dates";
import { enProse, nommer } from "@/ui/matieres";

/** Ce que le serveur rend, des deux côtés : la route du plan et le tableau de bord. */
export type Penurie = {
  cause: string;
  formes_attendues?: string[] | null;
  redisponible_le?: string | null;
};

/** « 859 – 1059 », sans décimale : une difficulté Elo n'a pas de dixièmes lisibles. */
export function bornes(bas: number, haut: number): string {
  return `${Math.round(bas)} – ${Math.round(haut)}`;
}

/**
 * Les formes de restitution, dites en français plutôt qu'en identifiant.
 *
 * « parent_validated » à l'écran d'un parent est un message d'erreur déguisé :
 * il a l'air d'une panne, alors qu'il décrit un exercice qui se fait sur cahier
 * et se relit à la main.
 */
export const FORMES: Record<string, string> = {
  flash: "question flash",
  free_input: "réponse saisie",
  parent_validated: "exercice sur cahier, relu par vous",
};

export function formesEnClair(formes: string[]): string {
  const dites = formes.map((forme) => FORMES[forme] ?? forme);
  if (dites.length <= 1) return dites[0] ?? "aucune forme connue";
  return `${dites.slice(0, -1).join(", ")} ou ${dites[dites.length - 1]}`;
}

export type Contexte = {
  /**
   * Ce qui suit la cause — et qui n'est vrai que d'un écran. Sous une liste
   * d'exercices, « la séance passera à la compétence suivante » ; sur la carte,
   * rien de tel n'est promis, puisqu'on ne sait pas si la compétence était même
   * candidate ce soir.
   */
  suite?: string;
  /** La plage réellement fouillée, quand l'écran la connaît. */
  bandeFouillee?: string;
};

/**
 * Laquelle des quatre causes, en une phrase de parent.
 *
 * `null` n'est pas « tout va bien » et ne se dit donc pas ainsi : c'est
 * l'absence de pénurie, et le seul énoncé honnête est que des exercices sont à
 * portée. Le plafond par matière tient toujours — d'où « peut être proposée »,
 * jamais « sera proposée ».
 */
export function phraseDePenurie(penurie: Penurie, contexte: Contexte = {}): string {
  const suite = contexte.suite ? ` ${contexte.suite}` : "";
  switch (penurie.cause) {
    case "aucun_verifie":
      // La seule des quatre qui demande d'agir tout de suite, et la seule
      // qu'une nuit de génération répare seule.
      return (
        "Aucun exercice vérifié n'existe sur cette compétence, à aucune " +
        `difficulté : le pool n'a encore rien produit ici.${suite}`
      );
    case "hors_bande": {
      const fouillee = contexte.bandeFouillee ? ` (${contexte.bandeFouillee})` : "";
      return (
        "Des exercices vérifiés existent, mais aucun dans la plage fouillée " +
        `ce soir${fouillee} : ils sont rangés ailleurs sur l'échelle.${suite}`
      );
    }
    case "forme_inadaptee":
      // Permanent, et il faut le dire : « ce soir » ferait attendre un
      // déblocage qui ne viendra pas de lui-même.
      return (
        "Des exercices existent à cette difficulté, mais aucun sous la forme " +
        `que cette compétence demande — ${formesEnClair(penurie.formes_attendues ?? [])}. ` +
        `Cela ne se débloquera pas seul : il faut en générer.${suite}`
      );
    case "deja_faits": {
      // « faits », et non « vus » : la trace de service s'écrit à la
      // correction et non au tirage — un énoncé affiché puis abandonné reste
      // tirable (arbitrage du 26 août 2026).
      const quand = penurie.redisponible_le
        ? `Le plus ancien redevient tirable le ${enClair(penurie.redisponible_le)}`
        : "Ils redeviendront tirables à l'expiration du délai de reprise";
      return (
        "Tous les exercices de cette difficulté ont déjà été faits, et le " +
        `délai avant de les reproposer n'a pas expiré. ${quand}, ` +
        `plus tôt si le pool est regarni d'ici là.${suite}`
      );
    }
    default:
      return `Aucun exercice à proposer ce soir (${penurie.cause}).${suite}`;
  }
}


/** Ce que le serveur rend d'une matière du jour visé qui n'a rien donné. */
export type Manque = {
  matiere: string;
  cause: string;
  ouvertes?: number;
  /**
   * Le détail par cause. Typé `unknown` parce que c'est ce que le générateur de
   * schéma rend d'un `dict[str, int]` d'OpenAPI, et le forcer ici mentirait sur
   * ce qui a réellement été vérifié — la phrase ne s'en sert pas.
   */
  par_cause?: Record<string, unknown> | null;
  redisponible_le?: string | null;
};

/**
 * Pourquoi une **matière entière** du jour visé n'a rien donné.
 *
 * Trois causes de plus que les quatre du tirage, et elles sont antérieures :
 * `travaillee_ailleurs` — la matière est hors du périmètre de ce système depuis
 * le 15 septembre 2026, et il n'y a rien à faire —, `hors_matieres_principales`
 * — la matière a cours mais n'est pas de celles que l'enfant travaille ici, ce
 * qui est un réglage et non une pénurie — et `aucune_ouverte` — le programme n'y
 * ouvre rien à cette date, ce qui n'appelle aucun geste du tout. Les deux
 * premières se ressemblent et appellent des gestes opposés : l'une se lève d'un
 * `teacher-people set-main-subjects`, l'autre ne se lève de rien, et les
 * confondre enverrait un parent chercher un réglage sans effet. `hors_compte`
 * est le désaccord entre le compte du plan (paliers stricts) et la plage
 * réellement fouillée par le tirage.
 *
 * `avecNombres` est faux chez l'enfant : l'invariant §14.1 lui refuse tout
 * chiffre qui ressemble à une note, et « 21 chapitres, 21 sans exercice » est un
 * bulletin. Le parent, lui, doit pouvoir mesurer le trou.
 *
 * Le nom de la matière vient d'`ui/matieres` — `nommer` en tête de phrase,
 * `enProse` au milieu —, jamais d'une table écrite ici : c'est exactement la
 * duplication que ce module-là refuse.
 */
export function phraseDuManque(manque: Manque, avecNombres = false): string {
  const titre = nommer(manque.matiere);
  const dans = enProse(manque.matiere);
  const combien =
    avecNombres && manque.ouvertes ? ` (${manque.ouvertes} au programme du jour)` : "";
  switch (manque.cause) {
    case "travaillee_ailleurs":
      return (
        `${titre} a cours ce jour-là, mais se travaille ailleurs qu'ici : ` +
        "aucun exercice n'en sera proposé."
      );
    case "hors_matieres_principales":
      return (
        `${titre} a cours ce jour-là, mais ne fait pas partie des matières ` +
        "travaillées ici : rien n'en sera proposé tant qu'elle n'y est pas ajoutée."
      );
    case "aucune_ouverte":
      return `Le programme de ${dans} n'ouvre encore aucun chapitre à cette date.`;
    case "aucun_verifie":
      return `Aucun exercice vérifié n'existe encore en ${dans}${combien}.`;
    case "hors_bande":
      return (
        `Il existe des exercices en ${dans}${combien}, mais aucun à la ` +
        "difficulté visée ce soir."
      );
    case "hors_compte":
      return (
        `Les exercices de ${dans}${combien} sont à la limite de la plage visée ` +
        "ce soir, et le plan ne les a pas comptés."
      );
    case "forme_inadaptee":
      return (
        `Les exercices de ${dans}${combien} n'existent pas sous la forme que ces ` +
        "chapitres demandent."
      );
    case "deja_faits": {
      const quand = manque.redisponible_le
        ? ` Le premier redevient proposable le ${enClair(manque.redisponible_le)}.`
        : "";
      return `Tout ce qui était proposable en ${dans}${combien} a déjà été fait.${quand}`;
    }
    default:
      return `Rien à proposer en ${dans} ce jour-là (${manque.cause}).`;
  }
}
